import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import useLenis from "../hooks/useLenis";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import FloatingIcons from "../components/site/FloatingIcons";

const SERVICES = [
    {
        id: 1,
        title: "PERFORMANCE MARKETING",
        copy: "Performance Marketing focuses on driving measurable digital growth through a results-oriented and data-driven approach. As the best performance marketing agency in Indore, Commmmuniti leverages digital marketing strategy to optimise campaigns around actions such as clicks, leads, and sales, ensuring maximum ROI and meaningful business impact.",
    },
    {
        id: 2,
        title: "SOCIAL MEDIA MARKETING",
        copy: "Social media marketing involves creating and sharing content across platforms to connect with your target audience, promote your offerings, and build lasting customer relationships. At Commmmuniti, providing social media marketing services in Indore, we craft strategic campaigns that enhance visibility, engagement, and brand growth.",
    },
    {
        id: 3,
        title: "SEARCH ENGINE OPTIMIZATION",
        copy: "Search Engine Optimisation (SEO) improves your website's visibility on search engines like Google, helping attract organic traffic and improve rankings. Through professional SEO services in Indore, Commmmuniti helps brands reach the right audience, strengthen online presence, and achieve long-term digital growth.",
    },
    {
        id: 4,
        title: "BROCHURE & EVENT DESIGN",
        copy: "We design impactful brochures and event creatives that bring ideas to life. From wedding invites and birthday designs to menus, standees, and event collaterals, we create visuals that are both functional and visually engaging.",
    },
    {
        id: 5,
        title: "BRAND STRATEGY",
        copy: "Brand strategy is the foundation that aligns your identity, messaging, and marketing approach to create a consistent brand presence. Through effective branding and marketing strategy, businesses can build stronger customer relationships, improve recognition, and drive long-term growth.",
    },
    {
        id: 6,
        title: "WEBSITE DESIGN",
        copy: "Your website is the digital face of your brand. We are a leading digital marketing agency who create websites that blend aesthetics, functionality, and user experience to build trust, boost engagement, and drive conversions.",
    },
    {
        id: 7,
        title: "EVENT MANAGEMENT",
        copy: "Hosting an event covers the complete planning and execution of impactful experiences such as conferences, launches, and corporate events. We ensure seamless coordination, creative direction, and smooth on-ground management for memorable brand experiences.",
    },
    {
        id: 8,
        title: "ACCOUNTING & ADVISORY SERVICES",
        copy: "Complete bookkeeping and accounting services to keep your finances organized and up-to-date and Strategic financial guidance to help you make informed decisions for your business growth.",
    },
];

export default function Services() {
    useLenis();
    const [open, setOpen] = useState(1);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (
        <div data-testid="services-page" className="relative">
            <Navbar />

            {/* HERO */}
            <section
                data-testid="services-hero"
                className="relative min-h-[80svh] w-full overflow-hidden bg-white pt-36 lg:pt-44 pb-24"
            >
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-[0.5]"
                    style={{
                        backgroundImage:
                            "radial-gradient(rgba(15,61,122,0.07) 1px, transparent 1px)",
                        backgroundSize: "26px 26px",
                    }}
                />
                <FloatingIcons tone="light" />

                <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10">
                    <p className="label-eyebrow">Our Services</p>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-5 font-display font-semibold text-[8vw] sm:text-5xl lg:text-[5rem] tracking-[0.06em] leading-[0.9] text-communiti-text"
                    >
                        OUR{" "}
                        <span className="text-communiti">SERVICES</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.2 }}
                            className="mt-8 font-display text-xl lg:text-[1.5rem] text-communiti-text max-w-3xl leading-tight"
                    >
                        Creative solutions. Strategic execution.{" "}
                        <span className="font-serif-it text-communiti">
                            Meaningful growth.
                        </span>
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.4 }}
                        className="mt-6 max-w-2xl text-base text-communiti-muted leading-relaxed"
                    >
                        From performance marketing and social media management to SEO,
                        branding, websites, and event experiences, we help brands build
                        visibility, engagement, and measurable business impact.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.6 }}
                        className="mt-10 flex items-center gap-3 flex-wrap"
                    >
                        <a
                            href="tel:+917987924110"
                            className="group inline-flex items-center gap-2 rounded-full bg-black text-white pl-6 pr-2 py-2.5 text-sm font-medium hover:bg-communiti transition-colors"
                        >
                            LET'S TALK
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-black group-hover:rotate-45 transition-transform duration-300">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </a>
                        <a
                            href="/#results"
                            className="group inline-flex items-center gap-2 rounded-full border border-communiti-text/15 bg-white pl-6 pr-2 py-2.5 text-sm font-medium hover:border-communiti hover:text-communiti transition-colors"
                        >
                            BROWSE PORTFOLIO
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-communiti-surface text-communiti-text group-hover:bg-communiti group-hover:text-white transition-all duration-300">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* SHOWCASE — Accordion */}
            <section
                data-testid="services-showcase"
                className="relative text-white py-24 lg:py-32 overflow-hidden"
                style={{ backgroundColor: "#0d3a6e" }}
            >
                <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 text-center pb-16">
                    <p className="label-eyebrow-light">What We Build</p>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                            className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight"
                    >
                        Eight practices.
                        <br />
                        <span className="font-serif-it text-black">
                            One creative collective.
                        </span>
                    </motion.h2>
                </div>

                <div className="relative mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10">
                    <div className="rounded-[28px] border border-white/15 bg-white/[0.04] backdrop-blur-sm overflow-hidden">
                        {SERVICES.map((s, idx) => {
                            const isOpen = s.id === open;
                            return (
                                <div
                                    key={s.id}
                                    data-testid={`services-row-${s.id}`}
                                    className={`border-t border-white/15 ${idx === 0 ? "border-t-0" : ""}`}
                                >
                                    <button
                                        onClick={() => setOpen(isOpen ? 0 : s.id)}
                                        data-testid={`services-toggle-${s.id}`}
                                        className="group w-full text-left px-6 sm:px-12 py-7 sm:py-9 flex items-center justify-between gap-6"
                                    >
                                        <div className="flex items-baseline gap-6 sm:gap-10 min-w-0 flex-1">
                                            <span
                                                className={`shrink-0 font-display text-sm sm:text-base md:text-xl ${isOpen ? "text-[#e6e6e6]" : "text-white/45"}`}
                                            >
                                                0{s.id}
                                            </span>
                                             <span
                                                 className={`font-display tracking-[-0.02em] text-[5vw] sm:text-[3vw] md:text-[2.3vw] leading-[0.9] ${isOpen ? "text-white" : "text-outline text-outline-hover"}`}
                                            >
                                                {s.title}
                                            </span>
                                        </div>
                                        <motion.span
                                            animate={{ rotate: isOpen ? 45 : 0 }}
                                            transition={{
                                                duration: 0.35,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            className={`shrink-0 inline-flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full border ${
                                                isOpen
                                                    ? "border-black text-black bg-white"
                                                    : "border-white/30 text-white/70 group-hover:border-white/80"
                                            }`}
                                        >
                                            <ArrowUpRight className="h-5 w-5" />
                                        </motion.span>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{
                                                    duration: 0.5,
                                                    ease: [0.16, 1, 0.3, 1],
                                                }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-6 sm:px-12 pb-10 lg:pb-14">
                                                    <div className="max-w-4xl ml-0 sm:ml-[68px]">
                                                        <p className="text-xs uppercase tracking-[0.22em] text-white/70">
                                                            Overview
                                                        </p>
                                                        <p className="mt-4 text-base lg:text-lg leading-relaxed text-white/90">
                                                            {s.copy}
                                                        </p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
